import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useTenant } from '../context/TenantContext';
import { isIOSApp } from '../utils/platform';

/**
 * PlanGate — bloquea acceso a una sección según el plan del negocio.
 *
 * Uso:
 *   <PlanGate feature="promotions">
 *     <PromotionsPage />
 *   </PlanGate>
 *
 * Features disponibles:
 *   memberships | gift_cards | promotions | customer_portal |
 *   commissions_advanced | employees_unlimited | whatsapp_agent | priority_support
 */
const PlanGate = ({ feature, children, inline = false }) => {
    const { hasFeature } = useTenant();
    const navigate = useNavigate();

    if (hasFeature(feature)) return <>{children}</>;

    // Bloqueo en pantalla completa
    if (!inline) {
        return (
            <div style={{
                minHeight: '60vh',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '2rem',
                fontFamily: 'var(--font-ui)'
            }}>
                <div style={{
                    maxWidth: '420px', width: '100%', textAlign: 'center',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-lg)', padding: '2.5rem 2rem',
                    boxShadow: 'var(--shadow-lg)'
                }}>
                    <div style={{
                        width: '64px', height: '64px', borderRadius: 'var(--radius-lg)',
                        background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 1.5rem'
                    }}>
                        <Lock size={30} color="#818cf8" />
                    </div>
                    <h2 style={{ color: 'var(--text-main)', fontWeight: '800', fontSize: '1.3rem', marginBottom: '0.75rem' }}>
                        Función no disponible
                    </h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '1.75rem' }}>
                        Esta función no está incluida en tu plan actual.<br />
                        {isIOSApp() ? 'Gestiona tu plan desde la web.' : 'Actualiza tu plan para desbloquearla.'}
                    </p>
                    {/* Apple 3.1.3(c): sin CTA a planes en el binario iOS */}
                    {!isIOSApp() && (
                        <button
                            onClick={() => navigate('/billing')}
                            style={{
                                width: '100%', padding: '0.875rem',
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                color: 'white', border: 'none', borderRadius: 'var(--radius-md)',
                                fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer',
                                boxShadow: '0 4px 20px rgba(99,102,241,0.35)'
                            }}
                        >
                            Ver Planes →
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Bloqueo inline (para ocultar elementos dentro de una página).
    // En iOS nativo el candado es solo visual: sin click a planes (3.1.3(c)).
    return (
        <div
            title={isIOSApp() ? 'No disponible en tu plan' : 'Actualiza tu plan para acceder'}
            onClick={isIOSApp() ? undefined : () => navigate('/billing')}
            style={{ opacity: 0.4, cursor: isIOSApp() ? 'default' : 'pointer', pointerEvents: 'auto', position: 'relative' }}
        >
            {children}
            <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.3)', borderRadius: 'var(--radius-sm)'
            }}>
                <Lock size={16} color="white" />
            </div>
        </div>
    );
};

export default PlanGate;
